import { useState } from "react";
import { DataTable, FilterBar, PageHeader, Paginacion } from "../../components/ui/patrones";
import { Aviso, Boton, Etiqueta, Modal } from "../../components/ui/primitivos";
import {
  type EventoAuditoria,
  type FiltroAuditoria,
  fetchAuditoria,
  fetchCatalogoAuditoria,
} from "../../lib/consola-api";
import { fechaHora } from "../../lib/formato";
import { useDatos } from "../../lib/useDatos";
import { etiquetaDeEvento, nombreDeEntidad, resumenDelEvento } from "./evento";

/**
 * Auditoría — porteada de `AuditLogPage` de `imprenta`.
 *
 * Mismas columnas —fecha, entidad, evento, **detalles** y usuario—, mismos
 * cinco filtros, y el JSON completo del payload en un modal. Es solo lectura:
 * los eventos los escriben las operaciones que auditan, nunca esta pantalla.
 *
 * La columna **Detalles** y la etiqueta legible del evento se agregaron con
 * ADR 0072: el comentario de acá las daba por hechas y no estaban, que es el
 * tipo de mentira documental que este repo ya pagó cuatro veces.
 */

const PAGINA = 50;

export function AuditoriaPagina() {
  const [filtro, setFiltro] = useState<FiltroAuditoria>({ limite: PAGINA, offset: 0 });
  const catalogo = useDatos(fetchCatalogoAuditoria);
  const eventos = useDatos(() => fetchAuditoria(filtro), JSON.stringify(filtro));
  const [detalle, setDetalle] = useState<EventoAuditoria | null>(null);

  const cambiar = (cambios: Partial<FiltroAuditoria>) =>
    // Todo cambio de filtro vuelve a la primera página: si no, se filtra y
    // aparece "no hay resultados" solo porque el offset quedó más allá del final.
    setFiltro((prev) => ({ ...prev, ...cambios, offset: 0 }));

  const offset = filtro.offset ?? 0;
  const tamano = filtro.limite ?? PAGINA;

  return (
    <div className="space-y-4">
      <PageHeader
        titulo="Registro de Auditoría"
        subtitulo="Historial completo de acciones y cambios en el sistema"
      />

      {eventos.error ? <Aviso>{eventos.error}</Aviso> : null}

      {/* Cuatro filtros: sin el contador, un listado filtrado se lee como uno
          vacío. `FilterBar` lo muestra y ofrece limpiar. */}
      <FilterBar
        filtrosActivos={
          [
            filtro.tipoEntidad,
            filtro.tipoEvento,
            filtro.entidadId,
            filtro.desde,
            filtro.hasta,
          ].filter(Boolean).length
        }
        alLimpiar={() => setFiltro({ limite: PAGINA, offset: 0 })}
      >
        <Filtro etiqueta="Tipo de Entidad">
          <select
            aria-label="Tipo de Entidad"
            value={filtro.tipoEntidad ?? ""}
            onChange={(e) => cambiar({ tipoEntidad: e.target.value || undefined })}
            className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">Todas las entidades</option>
            {(catalogo.datos?.tiposEntidad ?? []).map((t) => (
              <option key={t} value={t}>
                {nombreDeEntidad(t)}
              </option>
            ))}
          </select>
        </Filtro>

        <Filtro etiqueta="Tipo de Evento">
          <select
            aria-label="Tipo de Evento"
            value={filtro.tipoEvento ?? ""}
            onChange={(e) => cambiar({ tipoEvento: e.target.value || undefined })}
            className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          >
            <option value="">Todos los eventos</option>
            {(catalogo.datos?.tiposEvento ?? []).map((t) => (
              <option key={t} value={t}>
                {etiquetaDeEvento(t).texto}
              </option>
            ))}
          </select>
        </Filtro>

        {/* El filtro por id de entidad: el endpoint lo acepta desde siempre y
            ninguna pantalla lo mandaba. Es como se sigue la vida de UNA OT. */}
        <Filtro etiqueta="ID Entidad">
          <input
            aria-label="ID Entidad"
            value={filtro.entidadId ?? ""}
            onChange={(e) => cambiar({ entidadId: e.target.value || undefined })}
            placeholder="ID..."
            className="w-40 rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        </Filtro>

        <Filtro etiqueta="Desde">
          <input
            aria-label="Desde"
            type="date"
            value={filtro.desde ?? ""}
            onChange={(e) => cambiar({ desde: e.target.value || undefined })}
            className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        </Filtro>

        <Filtro etiqueta="Hasta">
          <input
            aria-label="Hasta"
            type="date"
            value={filtro.hasta ?? ""}
            onChange={(e) => cambiar({ hasta: e.target.value || undefined })}
            className="rounded-md border border-neutral-300 bg-white px-3 py-1.5 text-neutral-900 text-sm"
          />
        </Filtro>
      </FilterBar>

      <DataTable
        datos={eventos.datos?.eventos ?? []}
        clave={(e) => e.id}
        cargando={eventos.cargando}
        mensajeVacio="No hay eventos para ese filtro"
        columnas={[
          {
            clave: "fecha",
            encabezado: "Fecha",
            className: "whitespace-nowrap text-neutral-600",
            render: (e) => fechaHora(e.creadoEn),
          },
          {
            clave: "entidad",
            encabezado: "Entidad",
            render: (e) => (
              <>
                <Etiqueta>{nombreDeEntidad(e.tipoEntidad)}</Etiqueta>
                <span className="ml-2 font-mono text-neutral-500 text-xs">
                  {e.entidadId.slice(0, 12)}
                </span>
              </>
            ),
          },
          {
            clave: "evento",
            encabezado: "Evento",
            render: (e) => {
              const { texto, tono } = etiquetaDeEvento(e.tipoEvento);
              return <Etiqueta tono={tono}>{texto}</Etiqueta>;
            },
          },
          {
            // El resumen del payload. Sin esta columna hay que abrir el JSON de
            // cada fila para saber qué pasó, que es justo lo que una pantalla de
            // auditoría no debería pedir.
            clave: "detalles",
            encabezado: "Detalles",
            className: "text-neutral-600",
            render: (e) => resumenDelEvento(e.tipoEvento, e.payload),
          },
          {
            clave: "usuario",
            encabezado: "Usuario",
            className: "text-neutral-600",
            // `usuarioId` nulo = evento del sistema (cron, automatización).
            render: (e) => e.usuarioEmail ?? <span className="italic">sistema</span>,
          },
          {
            clave: "acciones",
            encabezado: "",
            className: "text-right",
            render: (e) => (
              <Boton variante="fantasma" onClick={() => setDetalle(e)}>
                Ver
              </Boton>
            ),
          },
        ]}
      />

      {/* El paginador compartido (ADR 0078), no uno propio. El de antes solo
          podía decir «hay una página más» —miraba si la página vino llena—, así
          que no sabía cuántas hay ni permitía saltar a la última. Por eso el
          endpoint ahora devuelve `total`.

          El corte lo hace el SERVIDOR y no la pantalla, que es lo distinto de
          los otros listados: la tabla de auditoría crece sola con cada acción
          del sistema, así que traerla entera para cortarla acá sería traer todo
          el historial en cada carga. `Paginacion` no lo nota: recibe la página
          y el total, y no le importa quién los calculó. */}
      <Paginacion
        pagina={Math.floor(offset / tamano) + 1}
        tamano={tamano}
        total={eventos.datos?.total ?? 0}
        alCambiarPagina={(p) => setFiltro((f) => ({ ...f, offset: (p - 1) * tamano }))}
        alCambiarTamano={(t) => setFiltro((f) => ({ ...f, limite: t, offset: 0 }))}
      />

      <Modal
        abierto={detalle !== null}
        onCerrar={() => setDetalle(null)}
        titulo={detalle ? etiquetaDeEvento(detalle.tipoEvento).texto : ""}
        descripcion={detalle ? `${nombreDeEntidad(detalle.tipoEntidad)}: ${detalle.entidadId}` : ""}
      >
        <pre className="max-h-[50vh] overflow-auto rounded-md bg-white p-3 text-neutral-700 text-xs">
          {JSON.stringify(detalle?.payload ?? {}, null, 2)}
        </pre>
        {detalle?.ipAddress ? (
          <p className="mt-2 text-neutral-500 text-xs">IP: {detalle.ipAddress}</p>
        ) : null}
      </Modal>
    </div>
  );
}

/** Un filtro con su rótulo arriba, como en `AuditLogPage`. */
function Filtro({ etiqueta, children }: { etiqueta: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <span className="block text-neutral-600 text-xs">{etiqueta}</span>
      {children}
    </div>
  );
}
